import React, { Suspense, useContext, useMemo } from 'react';
import { createBrowserRouter, createRoutesFromElements, Route, Outlet } from 'react-router';
// El RouterProvider "puro" de react-router no llama a ReactDOM.flushSync() en las
// actualizaciones de navegacion (para no romper createMemoryRouter en entornos sin DOM,
// como SSR/tests no-browser); el de react-router/dom si lo hace, que es lo recomendado
// para una SPA de navegador real como esta (F4-4, react-router v8 - antes venia
// via el paquete de conveniencia react-router-dom, removido en v8).
import { RouterProvider } from 'react-router/dom';
import Router from './Router';
import Context from './MainProvider/context';
import Unauthorized from './Unauthorized';
import Topbar from '../Layout/topbar/Topbar';
import Background from '../Layout/Background';
import SessionProvider from './SessionProvider';
import { NotificationProvider } from '../Notifications/NotificationContext';
import ScrollToTop from './ScrollToTop';

const loadingDiv = (
  <div className="load">
    <div className="load__icon-wrap">
      <svg className="load__icon">
        <path fill="#4ce1b6" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
      </svg>
    </div>
  </div>
);

// El shell del layout (Background/Topbar/ScrollToTop + el contenido de la ruta activa
// vía <Outlet/>) vive DENTRO del árbol del RouterProvider -es el `element` de la ruta
// raíz creada en AppContent- para que exista un único router para toda la app (ver
// PLAN_MIGRACION.md, F2-6): Topbar usa <Link>, así que necesita estar dentro del mismo
// contexto de router que el resto, no afuera envuelto en un <BrowserRouter> separado.
const AppShell: React.FC<{ isAuth: boolean }> = ({ isAuth }) => {
  const { mainLoading } = useContext(Context);

  return (
    <>
      {mainLoading && loadingDiv}
      <Suspense fallback={loadingDiv}>
        <div>
          <SessionProvider>
            <NotificationProvider>
              <div className="theme-light">
                <div className="wrapper">
                  <main>
                    <div>
                      <Background />
                      <ScrollToTop />
                      <Topbar isAuth={isAuth} />

                      <div className="container__wrap">
                        <Outlet />
                      </div>
                    </div>
                  </main>
                </div>
              </div>
            </NotificationProvider>
          </SessionProvider>
        </div>
      </Suspense>
    </>
  );
};

const AppContent: React.FC = () => {
  const { user } = useContext(Context);

  const isAuth = !!(user.fullName && user.roleCode);
  const block = Object.keys(user).length === 0;

  // Se recrea sólo cuando cambia isAuth/block (ej. login/logout) -no en cada render-,
  // igual que antes el ternario isAuth ? <Router/> : <Unauthorized/> sólo cambiaba de
  // rama en esos mismos momentos.
  const router = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <Route element={<AppShell isAuth={isAuth} />}>
            {isAuth ? (
              <Route path="/*" element={<Router />} />
            ) : (
              <Route path="/*" element={<Unauthorized block={block} />} />
            )}
          </Route>
        )
      ),
    [isAuth, block]
  );

  return <RouterProvider router={router} />;
};

export default React.memo(AppContent);
